import { useState, useEffect, useCallback } from 'react';
import SyncService from '../services/SyncService';

const STORAGE_KEY = 'quran_notes';

export const useNotes = () => {
  const [notes, setNotes] = useState([]);

  // Load notes from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setNotes(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  }, []);

  // Save notes to localStorage
  const saveNotes = useCallback((newNotes) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newNotes));
      setNotes(newNotes);
      SyncService.scheduleSync(); // ← cloud sync trigger
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  }, []);

  // Add or update a note
  const saveNote = useCallback((note) => {
    const id = `${note.surahNumber}-${note.ayahNumber}`;
    const newNote = {
      ...note,
      id,
      timestamp: Date.now(),
    };

    const updated = [
      ...notes.filter(n => n.id !== id),
      newNote,
    ];
    saveNotes(updated);
  }, [notes, saveNotes]);

  // Delete a note
  const deleteNote = useCallback((surahNumber, ayahNumber) => {
    const id = `${surahNumber}-${ayahNumber}`;
    const updated = notes.filter(n => n.id !== id);
    saveNotes(updated);
  }, [notes, saveNotes]);

  // Get a specific note
  const getNote = useCallback((surahNumber, ayahNumber) => {
    const id = `${surahNumber}-${ayahNumber}`;
    return notes.find(n => n.id === id);
  }, [notes]);

  // Check if a note exists
  const hasNote = useCallback((surahNumber, ayahNumber) => {
    const id = `${surahNumber}-${ayahNumber}`;
    return notes.some(n => n.id === id);
  }, [notes]);

  // Get notes grouped by surah
  const getGroupedNotes = useCallback(() => {
    const grouped = {};

    notes.forEach(note => {
      if (!grouped[note.surahNumber]) {
        grouped[note.surahNumber] = {
          surahName: note.surahName,
          surahEnglishName: note.surahEnglishName,
          notes: [],
        };
      }

      grouped[note.surahNumber].notes.push(note);
    });

    return grouped;
  }, [notes]);

  return {
    notes,
    saveNote,
    deleteNote,
    getNote,
    hasNote,
    getGroupedNotes,
    hasNotes: notes.length > 0,
  };
};

